import { useQuery } from '@apollo/client'
import { Grid, Text, TextSize, TextWeight } from '@focaldata/cin-ui-components'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  AudienceData,
  GET_AUDIENCES
} from '../../../../data/gql/questionnaire/queries/audiences'
import useGetDraftQuestionnaire from '../../../../hooks/questionnaire/useGetDraftQuestionnaire'
import { useFieldwork } from '../../../../hooks/useFieldwork'
import { useOptionalWaveId } from '../../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../../hooks/useProjectId'
import { useSurveyId } from '../../../../hooks/useSurveyId'
import {
  marketsForLanguage,
  visibleInAllMarkets,
  visibleInAnyMarket
} from '../../../../modules/Questionnaire/MarketVisibility/marketVisibility'
import ImportLanguageDocxDialog from '../../../../modules/Translations/ImportTranslations/ImportLanguageDocxDialog'
import AudienceTranslationCard from '../../../../modules/Translations/TranslationsEditor/AudienceTranslationCard'
import EntryTranslationCard from '../../../../modules/Translations/TranslationsEditor/EntryTranslationCard'
import RemoveLanguageDialog from '../../../../modules/Translations/TranslationsSidebar/RemoveLanguageDialog'
import TranslationsSidebar from '../../../../modules/Translations/TranslationsSidebar/TranslationsSidebar'
import { useAutoTranslate } from '../../../../modules/Translations/hooks/useAutoTranslate'
import { useDeclaredLanguages } from '../../../../modules/Translations/hooks/useDeclaredLanguages'
import { useDeleteLanguageTranslations } from '../../../../modules/Translations/hooks/useDeleteLanguageTranslations'
import { computeCompletion } from '../../../../modules/Translations/lib/completion'
import { extractCustomAudienceMembers } from '../../../../modules/Translations/lib/extractAudienceFields'
import { getTranslatableSurveyEntries } from '../../../../modules/Translations/lib/extractTranslatableFields'
import { languagesInUse } from '../../../../modules/Translations/lib/languagesInUse'
import { getLanguageLabel } from '../../../../utils/HelperFunctions'

// The Translations editor always treats English as the master / source
// language: the left column shows the authored (English) text regardless of
// which country the survey is fielded in, and the researcher translates
// outward into the fielding languages.
const MASTER_LANGUAGE = 'en'

const Translations: React.FC<{
  // Override for the target languages. Single-survey usage omits this and the
  // languages are derived from this survey's fieldwork allocations. Multi-market
  // usage passes the union of all markets' languages instead (languages are not
  // bound to a country — French covers both Canada and Belgium).
  availableLanguages?: { code: string; name: string }[]
  // Multi-market: only global screeners are translatable. Single-survey usage
  // omits this and keeps them all.
  globalAudienceOnly?: boolean
  // Multi-market: each market's fielded languages, used to hide questions no
  // market fielded in the active language shows.
  languagesBySurveyId?: ReadonlyMap<string, string[]>
}> = ({
  availableLanguages: availableLanguagesProp,
  globalAudienceOnly = false,
  languagesBySurveyId
}) => {
  const { draftQuestionnaire, refetchQuestionnaire } =
    useGetDraftQuestionnaire()
  const { fieldwork } = useFieldwork()
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const primaryLanguage = MASTER_LANGUAGE

  // Target languages come from the languages selected on the Audience page
  // (fieldwork.languageAllocations), NOT the full country catalogue. For
  // Belgium with French + Dutch allocations the researcher can translate
  // into French and Dutch; English stays the (untranslated) master column.
  // The sidebar excludes the master language and anything already declared,
  // so Canada (allocation [en, fr]) ends up offering just French.
  const derivedAvailableLanguages = useMemo(
    () =>
      (fieldwork?.languageAllocations ?? []).map((a) => ({
        code: a.language,
        name: getLanguageLabel(a.language)
      })),
    [fieldwork?.languageAllocations]
  )
  const availableLanguages = availableLanguagesProp ?? derivedAvailableLanguages

  const { data: audienceData, refetch: refetchAudience } =
    useQuery<AudienceData>(GET_AUDIENCES, {
      context: { clientName: 'questionnaire' },
      variables: { projectId, surveyId, waveId }
    })

  const audienceMembers = useMemo(
    () => audienceData?.draftAudience.members ?? [],
    [audienceData?.draftAudience.members]
  )

  // A question hidden in every market fielded in a language never reaches its
  // respondents. No market map (single-survey) filters nothing.
  const visibilityFor = useCallback(
    (languageCode: string) =>
      languagesBySurveyId
        ? visibleInAnyMarket(
            marketsForLanguage(languagesBySurveyId, languageCode)
          )
        : visibleInAllMarkets,
    [languagesBySurveyId]
  )

  // Unfiltered: removing a language must delete its translations everywhere,
  // including on questions it no longer shows.
  const translatableEntries = useMemo(
    () => getTranslatableSurveyEntries(draftQuestionnaire?.entries ?? []),
    [draftQuestionnaire?.entries]
  )

  const audienceGroups = useMemo(
    () =>
      extractCustomAudienceMembers(audienceMembers, {
        globalOnly: globalAudienceOnly
      }),
    [audienceMembers, globalAudienceOnly]
  )

  const derivedFromTranslations = useMemo(() => {
    const all = languagesInUse(translatableEntries, audienceMembers)
    // Translations are stored per-language on the backend with no country
    // binding — so French translations saved while the survey targeted
    // Canada will still be there after the Audience page switches the
    // country (and its language allocations) to e.g. Germany. Intersect
    // with the languages currently allocated on the Audience page so the
    // sidebar / target picker only show languages that are still in scope.
    // Stored translations for now-unallocated languages are kept (not
    // deleted) so re-allocating that language re-surfaces them.
    //
    // While fieldwork is still loading `availableLanguages` is [] — skip
    // filtering in that case to avoid briefly emptying the list.
    if (availableLanguages.length === 0) return all
    const allocatedCodes = new Set(availableLanguages.map((l) => l.code))
    return new Set(Array.from(all).filter((code) => allocatedCodes.has(code)))
  }, [translatableEntries, audienceMembers, availableLanguages])

  // Filter out primary language from available languages to pass to
  // useDeclaredLanguages — the hook excludes primaryLanguage anyway, but this
  // keeps the intent explicit and avoids adding it to the set only to remove it.
  const availableNonPrimary = useMemo(
    () => availableLanguages.filter((l) => l.code !== primaryLanguage),
    [availableLanguages, primaryLanguage]
  )

  const {
    languages: declaredLanguages,
    addLanguage,
    forgetLanguage
  } = useDeclaredLanguages({
    primaryLanguage,
    derivedFromTranslations,
    // Seed from allocated languages so they appear at 0% without manual add.
    // While fieldwork is loading `availableLanguages` is [], so the sidebar
    // starts empty and populates once the allocations arrive — no crash.
    availableLanguages: availableNonPrimary
  })

  const deleteLanguageTranslations = useDeleteLanguageTranslations()
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null)
  const [removalBusy, setRemovalBusy] = useState(false)

  // Drop a language from the declared list and, if it was the active one, fall
  // back to another declared language.
  const forgetAndReselect = (code: string) => {
    forgetLanguage(code)
    if (code === targetLanguage) {
      const remaining = declaredLanguages.filter((c) => c !== code)
      if (remaining.length > 0) setActiveLanguage(remaining[0])
    }
  }

  const confirmRemoval = async () => {
    if (!pendingRemoval) return
    setRemovalBusy(true)
    try {
      await deleteLanguageTranslations(
        translatableEntries,
        audienceMembers,
        pendingRemoval
      )
      // The delete mutations return the updated entry/member, but a field with
      // MANY deleted translations (one mutation per field) only ever proves
      // Apollo's cache normalization caught up for the exact fields it saw —
      // an explicit refetch is what makes useHasStoredTranslations (and so the
      // "Needs translation" checkbox and the launch gate) reliably see this
      // language's translations are gone, rather than depending on that.
      await Promise.all([refetchQuestionnaire(), refetchAudience()])
    } finally {
      forgetAndReselect(pendingRemoval)
      setRemovalBusy(false)
      setPendingRemoval(null)
    }
  }

  // A language with no stored translations has nothing to delete, so skip the
  // "this cannot be undone" confirmation and just drop it from the list.
  const handleRemoveLanguage = (code: string) => {
    const { translated } = computeCompletion(
      translatableEntries,
      code,
      audienceGroups
    )
    if (translated === 0) {
      forgetAndReselect(code)
    } else {
      setPendingRemoval(code)
    }
  }

  const [searchParams, setSearchParams] = useSearchParams()
  const langParam = searchParams.get('lang')
  const targetLanguage = langParam ?? declaredLanguages[0]

  // What the active language has to cover: cards, auto-translate and import.
  const targetVisibility = useMemo(
    () => visibilityFor(targetLanguage),
    [visibilityFor, targetLanguage]
  )
  const visibleEntries = useMemo(
    () =>
      getTranslatableSurveyEntries(
        draftQuestionnaire?.entries ?? [],
        targetVisibility
      ),
    [draftQuestionnaire?.entries, targetVisibility]
  )

  // If we have declared languages but no lang in URL, sync URL to the first
  // declared one so subsequent navigations carry the active selection.
  useEffect(() => {
    if (!langParam && declaredLanguages.length > 0) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('lang', declaredLanguages[0])
          return next
        },
        { replace: true }
      )
    }
  }, [langParam, declaredLanguages, setSearchParams])

  const setActiveLanguage = (code: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('lang', code)
      return next
    })
  }

  const { run: runAutoTranslate, progress: autoTranslateProgress } =
    useAutoTranslate()
  const [importOpen, setImportOpen] = useState(false)

  return (
    // Wrap the Grid in a sized div instead of putting maxWidth + padding +
    // margin directly on the Grid container. MUI's spacing prop applies a
    // negative margin on the container (-spacing/2 * 8px = -12px here) to
    // compensate for child gutter padding; combining that with maxWidth on
    // the same element causes the container to render 24px wider than the
    // declared maxWidth, producing the horizontal scroll we were seeing.
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
      <Grid container direction="row" spacing={3}>
        <Grid item xs={3}>
          {/*
            Sticky sidebar so the languages list / Import / Auto-translate
            controls stay in view as the researcher scrolls the long
            translation list on the right. `top: 24` matches the route's
            outer padding so the pinned position lines up with the natural
            top margin. This relies on MUI Grid's default
            `align-items: stretch` — it stretches this column to match the
            (much taller) right column, which gives `position: sticky` room
            to slide within while the right column scrolls past.
          */}
          <div style={{ position: 'sticky', top: 24 }}>
            <TranslationsSidebar
              targetLanguage={targetLanguage}
              declaredLanguages={declaredLanguages}
              primaryLanguage={primaryLanguage}
              availableLanguages={availableLanguages}
              entries={translatableEntries}
              audienceGroups={audienceGroups}
              visibilityFor={visibilityFor}
              autoTranslateProgress={autoTranslateProgress}
              onSelectLanguage={setActiveLanguage}
              onAddLanguage={addLanguage}
              onRemoveLanguage={handleRemoveLanguage}
              // Sequential, not parallel: useAutoTranslate exposes a single
              // shared `progress` state, so awaiting each language keeps the
              // counter coherent and the disabled flag honest.
              onAutoTranslate={async () => {
                for (const code of declaredLanguages) {
                  const isVisible = visibilityFor(code)
                  await runAutoTranslate(
                    getTranslatableSurveyEntries(
                      draftQuestionnaire?.entries ?? [],
                      isVisible
                    ),
                    code,
                    audienceGroups,
                    isVisible
                  )
                }
              }}
              onOpenImport={() => setImportOpen(true)}
            />
          </div>
        </Grid>
        <Grid item xs={9} container direction="column" spacing={2}>
          <Grid item>
            <Text size={TextSize.l} weight={TextWeight.SemiBold}>
              Translations
            </Text>
          </Grid>
          {declaredLanguages.length === 0 ? (
            <Grid item>
              <Text size={TextSize.m}>
                Add a language from the sidebar to start translating.
              </Text>
            </Grid>
          ) : (
            <>
              {audienceGroups.length > 0 && (
                <Grid item container direction="column" spacing={2}>
                  <Grid item>
                    <Text size={TextSize.m} weight={TextWeight.SemiBold}>
                      Custom screeners
                    </Text>
                  </Grid>
                  {audienceGroups.map((group) => (
                    <Grid item key={group.memberId}>
                      <AudienceTranslationCard
                        group={group}
                        targetLanguage={targetLanguage}
                        primaryLanguage={primaryLanguage}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
              <Grid item container direction="column" spacing={2}>
                {audienceGroups.length > 0 && (
                  <Grid item>
                    <Text size={TextSize.m} weight={TextWeight.SemiBold}>
                      Survey questions
                    </Text>
                  </Grid>
                )}
                {visibleEntries.length === 0 && audienceGroups.length === 0 ? (
                  <Grid item>
                    <Text size={TextSize.m}>
                      No translatable entries yet. Add questions in the Survey
                      tab or custom screeners on the Audience tab.
                    </Text>
                  </Grid>
                ) : (
                  visibleEntries.map((entry) => (
                    <Grid item key={entry.id}>
                      <EntryTranslationCard
                        entry={entry}
                        targetLanguage={targetLanguage}
                        primaryLanguage={primaryLanguage}
                        isVisible={targetVisibility}
                      />
                    </Grid>
                  ))
                )}
              </Grid>
            </>
          )}
        </Grid>
        {/* DOCX import keeps the narrow entry list (translatableEntries) for now;
            section title parity is tracked as COR-218. */}
        <ImportLanguageDocxDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          targetLanguage={targetLanguage}
          entries={visibleEntries}
          audienceGroups={audienceGroups}
          isVisible={targetVisibility}
        />
        <RemoveLanguageDialog
          open={pendingRemoval !== null}
          languageCode={pendingRemoval}
          busy={removalBusy}
          onConfirm={confirmRemoval}
          onClose={() => {
            if (!removalBusy) setPendingRemoval(null)
          }}
        />
      </Grid>
    </div>
  )
}

export default Translations
